import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, ArrowLeft, ShoppingBag, CreditCard, Lock, AlertCircle } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = ({ cart, sessionId, onOrderComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = info, 2 = payment
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [cloverReady, setCloverReady] = useState(false);
  const [cloverError, setCloverError] = useState(null);
  const cloverRef = useRef(null);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    pickup_time: "",
    notes: "",
  });

  // Load Clover SDK and initialize iframe elements
  useEffect(() => {
    if (step !== 2) return;

    const loadCloverSDK = async () => {
      try {
        // Get Clover config from backend
        const configResponse = await axios.get(`${API}/payments/config`);
        const { public_key, merchant_id, test_mode } = configResponse.data;

        if (!public_key) {
          setCloverError("Payment configuration not available");
          return;
        }

        // Determine SDK URL based on environment
        const sdkUrl = test_mode 
          ? "https://checkout.sandbox.dev.clover.com/sdk.js"
          : "https://checkout.clover.com/sdk.js";

        // Load Clover SDK script
        if (!window.Clover) {
          const script = document.createElement("script");
          script.src = sdkUrl;
          script.async = true;
          
          script.onload = () => {
            initializeClover(public_key, merchant_id);
          };
          
          script.onerror = () => {
            setCloverError("Failed to load payment system");
          };
          
          document.body.appendChild(script);
        } else {
          initializeClover(public_key, merchant_id);
        }
      } catch (error) {
        console.error("Error loading Clover:", error);
        setCloverError("Failed to initialize payment system");
      }
    };

    const initializeClover = (publicKey, merchantId) => {
      try {
        // Initialize Clover with public key and merchant ID
        const clover = new window.Clover(publicKey, {
          merchantId: merchantId
        });
        cloverRef.current = clover;

        const elements = clover.elements();

        // Custom styles for the iframe elements
        const styles = {
          input: {
            fontSize: '16px',
            fontFamily: '"Outfit", sans-serif',
            color: '#ffffff',
            backgroundColor: 'transparent',
            '::placeholder': {
              color: '#666666'
            }
          }
        };

        // Create individual card elements
        const cardNumber = elements.create('CARD_NUMBER', styles);
        const cardDate = elements.create('CARD_DATE', styles);
        const cardCvv = elements.create('CARD_CVV', styles);
        const cardPostalCode = elements.create('CARD_POSTAL_CODE', styles);

        // Mount elements to their containers
        setTimeout(() => {
          try {
            cardNumber.mount('#card-number');
            cardDate.mount('#card-date');
            cardCvv.mount('#card-cvv');
            cardPostalCode.mount('#card-postal-code');
            
            setCloverReady(true);

            // Add error listeners
            const handleError = (fieldName) => (event) => {
              if (event.error) {
                console.log(`${fieldName} error:`, event.error);
              }
            };

            cardNumber.addEventListener('change', handleError('Card Number'));
            cardDate.addEventListener('change', handleError('Card Date'));
            cardCvv.addEventListener('change', handleError('CVV'));
            cardPostalCode.addEventListener('change', handleError('Postal Code'));

          } catch (mountError) {
            console.error("Error mounting Clover elements:", mountError);
            setCloverError("Failed to load card input fields");
          }
        }, 500);

      } catch (error) {
        console.error("Error initializing Clover:", error);
        setCloverError("Failed to initialize card form");
      }
    };

    loadCloverSDK();
  }, [step]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      // Create order first
      const response = await axios.post(`${API}/orders`, {
        ...formData,
        session_id: sessionId,
      });
      
      setOrderId(response.data.id);
      setStep(2);
      toast.success("Order created! Please complete payment.");
    } catch (e) {
      console.error("Error creating order:", e);
      toast.error("Failed to create order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!cloverRef.current) {
      toast.error("Payment system not ready. Please wait...");
      return;
    }

    setLoading(true);
    setCloverError(null);

    try {
      // Create token from Clover - no element parameter needed
      const result = await cloverRef.current.createToken();

      if (result.errors) {
        const errorMessages = Object.values(result.errors).join(", ");
        setCloverError(errorMessages);
        toast.error("Card validation failed: " + errorMessages);
        setLoading(false);
        return;
      }

      if (!result.token) {
        setCloverError("Failed to process card");
        toast.error("Failed to process card. Please try again.");
        setLoading(false);
        return;
      }

      console.log("Clover token created:", result.token);

      // Send token to backend to process charge
      const response = await axios.post(`${API}/payments/charge`, {
        order_id: orderId,
        clover_token: result.token,
      });

      if (response.data.success) {
        toast.success("Payment successful!");
        // Clear the cart
        await axios.delete(`${API}/cart/${sessionId}`);
        onOrderComplete();
        navigate(`/order/${orderId}`);
      }
    } catch (e) {
      console.error("Payment error:", e);
      const errorMsg = e.response?.data?.detail || "Payment failed. Please try again.";
      setCloverError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const isEmpty = !cart?.items || cart.items.length === 0;

  if (isEmpty) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="checkout-empty">
        <div className="text-center p-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
            <ShoppingBag className="w-10 h-10 text-muted-foreground" />
          </div>
          <h2 className="font-unbounded text-2xl font-bold text-foreground mb-4">
            Your cart is empty
          </h2>
          <p className="font-outfit text-muted-foreground mb-6">
            Add some products before checking out
          </p>
          <Button asChild className="bg-primary text-primary-foreground">
            <Link to="/shop">Browse Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Button
            data-testid="back-to-shop-btn"
            variant="ghost"
            onClick={() => step === 2 ? setStep(1) : navigate(-1)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 2 ? "Back to Info" : "Back"}
          </Button>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                1
              </div>
              <span className="font-outfit hidden sm:inline">Information</span>
            </div>
            <div className="w-12 h-0.5 bg-border" />
            <div className={`flex items-center gap-2 ${step >= 2 ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                2
              </div>
              <span className="font-outfit hidden sm:inline">Payment</span>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-unbounded text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
            {step === 1 ? "Checkout" : "Payment"}
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            {step === 1 ? (
              /* Step 1: Customer Information */
              <div className="bg-card rounded-2xl border border-border p-6 md:p-8">
                <h2 className="font-unbounded text-xl font-semibold text-foreground mb-6">
                  Pickup Information
                </h2>

                {/* Store Pickup Notice */}
                <div className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-outfit font-semibold text-foreground">
                        Store Pickup Only
                      </p>
                      <p className="font-outfit text-sm text-muted-foreground">
                        1823 E Santa Fe St, Gardner, KS 66030
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleInfoSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="customer_name" className="font-outfit">
                      Full Name *
                    </Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      data-testid="checkout-name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="mt-2 bg-muted border-border focus:border-primary"
                    />
                  </div>

                  <div>
                    <Label htmlFor="customer_email" className="font-outfit">
                      Email Address *
                    </Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      data-testid="checkout-email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="mt-2 bg-muted border-border focus:border-primary"
                    />
                  </div>

                  <div>
                    <Label htmlFor="customer_phone" className="font-outfit">
                      Phone Number *
                    </Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      type="tel"
                      data-testid="checkout-phone"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      required
                      className="mt-2 bg-muted border-border focus:border-primary"
                    />
                  </div>

                  <div>
                    <Label htmlFor="pickup_time" className="font-outfit">
                      Preferred Pickup Time
                    </Label>
                    <div className="flex items-center gap-2 mt-1 mb-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="font-outfit text-xs text-muted-foreground">
                        Store Hours: 10 AM - 8 PM Daily
                      </span>
                    </div>
                    <Input
                      id="pickup_time"
                      name="pickup_time"
                      data-testid="checkout-pickup-time"
                      value={formData.pickup_time}
                      onChange={handleChange}
                      placeholder="e.g., Tomorrow at 2 PM"
                      className="bg-muted border-border focus:border-primary"
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes" className="font-outfit">
                      Order Notes (Optional)
                    </Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      data-testid="checkout-notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any special requests or notes for your order..."
                      rows={3}
                      className="mt-2 bg-muted border-border focus:border-primary resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="continue-to-payment-btn"
                    disabled={loading}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-outfit font-semibold py-6 text-lg rounded-xl neon-box"
                  >
                    {loading ? "Processing..." : "Continue to Payment"}
                  </Button>
                </form>
              </div>
            ) : (
              /* Step 2: Payment with Clover iframe */
              <div className="bg-card rounded-2xl border border-border p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="w-6 h-6 text-primary" />
                  <h2 className="font-unbounded text-xl font-semibold text-foreground">
                    Payment Details
                  </h2>
                </div>

                {/* Security Notice */}
                <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-green-500" />
                    <p className="font-outfit text-sm text-green-500">
                      Secure payment powered by Clover
                    </p>
                  </div>
                </div>

                {/* Error Display */}
                {cloverError && (
                  <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <p className="font-outfit text-sm text-red-500">
                        {cloverError}
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handlePaymentSubmit} className="space-y-6">
                  {/* Loading indicator */}
                  {!cloverReady && !cloverError && (
                    <div className="flex items-center justify-center gap-2 text-muted-foreground py-8">
                      <div className="w-5 h-5 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                      <span className="font-outfit text-sm">Loading secure payment form...</span>
                    </div>
                  )}

                  {/* Clover Card Elements */}
                  <div className={cloverReady ? "" : "hidden"}>
                    {/* Card Number */}
                    <div className="mb-4">
                      <Label className="font-outfit mb-2 block text-sm">Card Number</Label>
                      <div 
                        id="card-number" 
                        className="px-3 py-2 bg-muted border border-border rounded-lg h-11"
                      ></div>
                    </div>

                    {/* Card Date and CVV */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="font-outfit mb-2 block text-sm">Expiration</Label>
                        <div 
                          id="card-date" 
                          className="px-3 py-2 bg-muted border border-border rounded-lg h-11"
                        ></div>
                      </div>
                      <div>
                        <Label className="font-outfit mb-2 block text-sm">CVV</Label>
                        <div 
                          id="card-cvv" 
                          className="px-3 py-2 bg-muted border border-border rounded-lg h-11"
                        ></div>
                      </div>
                    </div>

                    {/* Postal Code */}
                    <div className="mb-6">
                      <Label className="font-outfit mb-2 block text-sm">ZIP Code</Label>
                      <div 
                        id="card-postal-code" 
                        className="px-3 py-2 bg-muted border border-border rounded-lg h-11"
                      ></div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="place-order-btn"
                    disabled={loading || !cloverReady}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-outfit font-semibold py-6 text-lg rounded-xl neon-box disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                        Processing Payment...
                      </div>
                    ) : (
                      `Pay $${cart.total?.toFixed(2) || "0.00"}`
                    )}
                  </Button>
                </form>
              </div>
            )}
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-card rounded-2xl border border-border p-6 md:p-8 sticky top-24">
              <h2 className="font-unbounded text-xl font-semibold text-foreground mb-6">
                Order Summary
              </h2>

              {/* Cart Items */}
              <div className="space-y-4 mb-6">
                {cart.items.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex gap-4"
                    data-testid={`summary-item-${item.product_id}`}
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-outfit text-sm font-medium text-foreground truncate">
                        {item.product_name}
                      </h4>
                      <p className="font-outfit text-xs text-muted-foreground">
                        Qty: {item.quantity} × ${item.price.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="font-outfit text-sm font-semibold text-foreground">
                        ${item.item_total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <Separator className="mb-6 bg-border" />

              {/* Totals */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-outfit text-muted-foreground">
                    Subtotal
                  </span>
                  <span className="font-outfit text-foreground">
                    ${cart.subtotal?.toFixed(2) || "0.00"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-outfit text-muted-foreground">
                    Tax ({((cart.tax_rate || 0.0948) * 100).toFixed(2)}%)
                  </span>
                  <span className="font-outfit text-foreground">
                    ${cart.tax?.toFixed(2) || "0.00"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-outfit text-muted-foreground">
                    Shipping
                  </span>
                  <span className="font-outfit text-green-500">
                    FREE (Pickup)
                  </span>
                </div>
                <Separator className="bg-border" />
                <div className="flex justify-between">
                  <span className="font-unbounded text-lg font-semibold text-foreground">
                    Total
                  </span>
                  <span className="font-unbounded text-xl font-bold text-primary">
                    ${cart.total?.toFixed(2) || "0.00"}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
